import React from "react";
import { Redirect, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import LibraryMusicIcon from "@material-ui/icons/LibraryMusic";
import HowToVoteIcon from "@material-ui/icons/HowToVote";
import { UserContext } from "../../contexts/UserContext";
import logo from "../../assets/logo.png";

const useStyles = makeStyles((theme) => ({
  hero: {
    padding: theme.spacing(4, 3, 3),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 2, 1),
    },
  },
  tagline: {
    maxWidth: 640,
    margin: "0 auto",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
  },
  ctaButton: {
    margin: theme.spacing(3),
  },
  featuresSection: {
    padding: theme.spacing(1, 3, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5, 2, 1),
    },
  },
  logo: {
    height: 96,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      height: 64,
    },
  },
  featureCard: {
    height: "100%",
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  featureIcon: {
    fontSize: 32,
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.main,
    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
      marginBottom: theme.spacing(0.5),
    },
  },
  // Mobile: cards show just the title, and flip in place on tap to
  // reveal the description on their back face.
  flipCardScene: {
    height: 130,
    perspective: 1000,
    cursor: "pointer",
  },
  flipCard: {
    position: "relative",
    width: "100%",
    height: "100%",
    transition: "transform 0.6s",
    transformStyle: "preserve-3d",
  },
  flipCardFlipped: {
    transform: "rotateY(180deg)",
  },
  flipCardFace: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    textAlign: "center",
  },
  flipCardBack: {
    transform: "rotateY(180deg)",
  },
  footer: {
    padding: theme.spacing(2, 3),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 2),
    },
  },
}));

const FeatureCard = ({ icon: Icon, title, description, classes, isMobile }) => {
  const [flipped, setFlipped] = React.useState(false);

  if (!isMobile) {
    return (
      <Paper className={classes.featureCard} elevation={2}>
        <Icon className={classes.featureIcon} />
        <Typography component="h2" variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      className={classes.flipCardScene}
      elevation={2}
      onClick={() => setFlipped((prev) => !prev)}
      role="button"
      tabIndex={0}
      onKeyPress={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          setFlipped((prev) => !prev);
        }
      }}
      aria-pressed={flipped}
      aria-label={`${title}. Tap for details.`}
    >
      <div
        className={`${classes.flipCard} ${flipped ? classes.flipCardFlipped : ""}`}
      >
        <div className={classes.flipCardFace}>
          <Icon className={classes.featureIcon} />
          <Typography component="h2" variant="h6">
            {title}
          </Typography>
        </div>
        <div className={`${classes.flipCardFace} ${classes.flipCardBack}`}>
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        </div>
      </div>
    </Paper>
  );
};

const features = [
  {
    icon: EventIcon,
    title: "Calendar",
    description:
      "Plan and view upcoming events, complete with an ordered song list, " +
      "lyrics, and YouTube videos for each song. Team leaders can " +
      "drag-and-drop to reorder songs and export event details for " +
      "WhatsApp.",
  },
  {
    icon: LibraryMusicIcon,
    title: "Catalog",
    description:
      "A searchable catalog of every song your team knows, with lyrics " +
      "and video included, so your team can find and prepare songs " +
      "quickly.",
  },
  {
    icon: HowToVoteIcon,
    title: "Song Proposals",
    description:
      "Propose new songs for your team to learn, and vote on songs " +
      "others have proposed. Team leaders can promote popular proposals " +
      "into the catalog.",
  },
];

// A stable, always-public page for external use (e.g. as the
// application homepage registered on an OAuth consent screen).
// `redirectIfAuthenticated` additionally sends already-logged-in users
// straight to their calendar, used when this is mounted at "/".
export const Home = ({ redirectIfAuthenticated }) => {
  const classes = useStyles();
  const userContext = React.useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (redirectIfAuthenticated && userContext.user.isAuthenticated) {
    return <Redirect to="/song-proposals" />;
  }

  return (
    <Box>
      <Box className={classes.hero}>
        <img src={logo} alt="" className={classes.logo} />
        <Typography component="h1" variant="h3">
          PlanPraise
        </Typography>
        <Typography
          className={classes.tagline}
          variant="h6"
          color="textSecondary"
        >
          A tool for worship teams to plan events, organize their song
          catalog, and propose and vote on new songs to learn.
        </Typography>
        <Box display="flex" justifyContent="center" flexWrap="wrap">
          <Button
            className={classes.ctaButton}
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/login"
          >
            Sign In
          </Button>
          <Button
            className={classes.ctaButton}
            variant="outlined"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/request-access"
          >
            Sign Up Your Team
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" className={classes.featuresSection}>
        <Grid container spacing={isMobile ? 1 : 4}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <FeatureCard {...feature} classes={classes} isMobile={isMobile} />
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box className={classes.footer}>
        <Typography variant="body2" color="textSecondary">
          <Link color="textSecondary" href="/privacy_policy">
            Privacy Policy
          </Link>
          {" · "}
          <Link color="textSecondary" href="/terms_of_service">
            Terms of Service
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
